/**
 * The Finance workspace (workforce write cutover, D-XF-32; packet §26; the
 * board view added in Tide phase 2, 2026-10-04 - see FinanceBoard.tsx).
 *
 * One resident, the Finance slice, in the order a Finance rep asks: who and
 * where · current Finance work · balance, terms, aging · who pays and who
 * decides · approval and UMED · provider and next visit · documents and
 * recordings · recent history · actions (ResidentDossier.tsx). The board is
 * its own view and opens this same dossier under a row. No clinical detail.
 *
 * Every fact says where it came from; a fact with two sources is shown with
 * both. Finance writes only its own notes and the expedited-dispense request
 * that Scheduling decides.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import WorkspaceMenu from '../../components/WorkspaceMenu';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  financeApi, outcomeOf, money, freshKey,
  FinanceScope, ResidentHit, ResidentContext,
} from './financeApi';
import { Context } from './ResidentDossier';
import FinanceBoard from './FinanceBoard';
// 2026-10-13: the Board layout (operational Kanban) beside the table; the Kanban is the default since 2026-10-14.
import FinanceKanban from './FinanceKanban';
// 2026-10-15: DCF resubmittals as their own Kanban view.
import ResubmittalsKanban from './ResubmittalsKanban';
import { useBoardLayout, type BoardLayout } from '../../components/board/kanban/layout';
// 2026-10-14: the views moved from the buttons above the board to the workspace sidebar (Isaiah Cotto).
import { WorkspaceLayout, SidebarTrigger, type SidebarGroup } from '../../components/workspaceSidebar';
import { HandCoins, HeartOff, PackageCheck, ReceiptText, RotateCcw, Scale, Search, SquareKanban, Table2 } from 'lucide-react';
import DeathReports, { useDeathRequestCount } from './DeathReports';
import '../umed/umed.css';
import './finance.css';
import { shortDate as fmtDate, saleTermsLine, payerLine, PRIVATE_PAY_LABEL, awaitingCorrectionLine, AWAITING_CORRECTION_WHY, hitStateWord, correctedWord } from './financeApi';
import type { HandoffItem } from './financeApi';
import { SendBackAction } from '../../components/sendBack/SendBackDialog';
import { sendBackPath } from '../../components/sendBack/sendBackApi';
import CutoverReconciliation, { useReconAvailable } from './recon/CutoverReconciliation';
import LedgerImportPage from './LedgerImportPage';
import AtdResidentsBoard, { useAtdCan } from '../../components/atd/AtdResidentsBoard';
import BuildingOrClinic from '../../components/emerge/BuildingOrClinic';
import { FindResidentEntry } from '../../components/residentDirectory';

/*
 * A gateway answer (502/503/504) comes from the proxy in front of the server, not from the server: the receive may
 * have committed behind it (Copilot on #377, P2). It is never "Nothing was saved". A retry is safe -- a handoff is
 * received once per UMED obligation and a second receive answers the first (financeHandoffService.receive) -- but the
 * list is read again first, so Finance sees whether it went through.
 */
const GATEWAY_STATUSES = [502, 503, 504];
const RECEIVE_UNSURE = 'The server did not answer in time, so this may have been saved. Refresh the page before trying again: if it was received, this handoff no longer offers Receive.';

/**
 * Finance's inbox (cutover 2026-09-21): every UMED→Finance handoff that is
 * still open for a resident in scope, oldest first. Until now the handoff
 * obligation was created on the UMED case and listed nowhere Finance looks.
 * Opening one is opening the resident; the obligation itself stays UMED's.
 */
function Handoffs({ onOpen, canSendBack = false, canManage = false }: { onOpen: (residentId: string) => void; canSendBack?: boolean; canManage?: boolean }) {
  const [items, setItems] = useState<HandoffItem[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // Bumped after a send-back closes, so the list re-reads: a returned handoff is no longer Finance's.
  const [nonce, setNonce] = useState(0);
  // Receive (the first handoff act): named by the UMED obligation, which only this list knows. One key per opening of the row, reused on a retry.
  const [receiving, setReceiving] = useState<string | null>(null);
  const [receiveErr, setReceiveErr] = useState<{ id: string; message: string } | null>(null);
  const receiveKeys = useRef<Record<string, string>>({});
  const receive = async (actionId: string) => {
    if (receiving) return;
    setReceiving(actionId); setReceiveErr(null);
    if (!receiveKeys.current[actionId]) receiveKeys.current[actionId] = freshKey();
    try {
      const r = await financeApi.receiveHandoff(actionId, receiveKeys.current[actionId]);
      if (r.success) { delete receiveKeys.current[actionId]; setNonce((n) => n + 1); }
      else setReceiveErr({ id: actionId, message: r.status === undefined ? 'Could not reach the server; it may or may not have been recorded. Try again: the same submission will not be recorded twice.' : GATEWAY_STATUSES.includes(r.status) ? RECEIVE_UNSURE : outcomeOf(r, { wrote: true }) });
    } catch { setReceiveErr({ id: actionId, message: 'Could not reach the server; it may or may not have been recorded. Try again: the same submission will not be recorded twice.' }); }
    finally { setReceiving(null); }
  };
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const r = await financeApi.approvals();
        if (!live) return;
        if (r.success && r.data) { setItems(r.data.items); return; }
        setItems(null); setErr(outcomeOf(r));
      } catch { if (live) { setItems(null); setErr('Could not reach the server.'); } }
    })();
    return () => { live = false; };
  }, [nonce]);
  return (
    <section className="um-card" aria-label="Approvals handed to Finance">
      <div className="um-card-head"><div><h2>Handed to Finance</h2><p className="why">UMED approvals and private-pay sales waiting for Finance, oldest first. A private-pay sale comes straight from the Patient Advocate with no UMED, and says so. One approved UMED is one handoff: a correction or DCF resubmittal completed after it shows on that handoff as "Corrected", never as a second one. Open one to work the resident.</p></div></div>
      {err && <p className="um-notice stop" role="alert" style={{ margin: 12 }}>{err}</p>}
      {!err && items === null && <p className="um-quiet" style={{ padding: 12 }}>Loading…</p>}
      {items && items.length === 0 && <p className="um-quiet" style={{ padding: 12 }}>Nothing waiting. A UMED approval or a private-pay sale handed over in eMerge will appear here.</p>}
      {items && items.length > 0 && (
        <ul className="fn-rows" aria-label="Handoffs">
          {items.map((h) => {
            const pp = h.kind === 'PRIVATE_PAY';
            const terms = pp ? saleTermsLine(h.sale) : null;
            return (
            <li key={h.action_id || h.handoff_id || h.handoff?.id}>
              <button type="button" className="fn-row" onClick={() => onOpen(h.resident.id)}>
                <span className="who">{h.resident.name}</span>
                {pp ? (
                  <>
                    <span className="where"><BuildingOrClinic row={h} fallback="No current building" /> · handed {fmtDate(h.handed_at)}{h.handed_by_name ? ` by ${h.handed_by_name}` : ''}</span>
                    <span className="state">{terms || 'Sale terms not recorded'} · {payerLine(h.sale?.payer)}{h.finance_status ? ` · ${h.finance_status}` : ' · no Finance account yet'}{h.finance_notes ? ` · ${h.finance_notes} note${h.finance_notes === 1 ? '' : 's'}` : ''}</span>
                    <span className="fn-pp">{h.label || PRIVATE_PAY_LABEL}</span>
                  </>
                ) : (
                  <>
                    <span className="where"><BuildingOrClinic row={h} fallback="No current building" /> · handed {fmtDate(h.handed_at)}{h.umed_operator_name ? ` by ${h.umed_operator_name}` : ''}</span>
                    <span className="state">{h.approval.proven ? `Approved in eMerge${h.approval.noca === 'RECEIVED' ? ' · NOCA received' : ''}` : h.creation_reason ? `Correction completed · ${h.creation_reason.toLowerCase().replace(/_/g, ' ')}` : 'Handed off'}{h.finance_status ? ` · ${h.finance_status}` : ' · no Finance account yet'}{h.finance_notes ? ` · ${h.finance_notes} note${h.finance_notes === 1 ? '' : 's'}` : ''}</span>
                  </>
                )}
                {/* Ruling #1 (2026-10-07): a correction or reversal after the handoff is an amendment on THIS row, never a second row. */}
                {h.corrected && h.corrected.count > 0 && (
                  <span className="fn-corrected" title={`${h.corrected.count} amendment${h.corrected.count === 1 ? '' : 's'} on this handoff`}>
                    {correctedWord(h.corrected)} {fmtDate(h.corrected.last_on)}{h.corrected.count > 1 ? ` · ${h.corrected.count} amendments` : ''}
                  </span>
                )}
                {/* PAR 01 (2026-10-08): APPROVED - SFS CORRECTION -- the approved months are collectable; these wait on the correction. */}
                {h.awaiting_correction && (
                  <span className="fn-corrected" title={AWAITING_CORRECTION_WHY}>{awaitingCorrectionLine(h.awaiting_correction)}</span>
                )}
                {/* Ruling R1 (2026-10-11): the case's Monday Finance row rides on the handoff as a reference; check it before invoicing. */}
                {h.monday_reference && (
                  <span className="fn-corrected" title={h.monday_reference.notice}>Monday Finance row: check before invoicing</span>
                )}
              </button>
              {/* Receive: a UMED obligation Finance has no record of yet. A private-pay handoff arrives already received. */}
              {canManage && !pp && !h.handoff && h.action_id && (
                <div className="fn-sendback">
                  <button type="button" className="um-open" disabled={receiving !== null} onClick={() => receive(h.action_id as string)}>{receiving === h.action_id ? 'Receiving…' : `Receive handoff for ${h.resident.name}`}</button>
                  {receiveErr && receiveErr.id === h.action_id && <p className="um-notice stop" role="alert">{receiveErr.message}</p>}
                </div>
              )}
              {/* Only the person who accepted a handoff holds it, so only an ACCEPTED one is offered; the preview and the server decide again. A private-pay handoff never goes back to UMED. */}
              {canSendBack && !pp && h.handoff && h.handoff.state === 'ACCEPTED' && (
                <div className="fn-sendback">
                  <SendBackAction
                    path={sendBackPath.finance(h.handoff.id)} recordLabel={h.resident.name} className="um-open" label="Send back to UMED…"
                    onClosed={(done) => { if (done) setNonce((n) => n + 1); }}
                  />
                </div>
              )}
            </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

const BASE = '/portal/finance';

function Refusal({ title, body }: { title: string; body: string }) {
  return <section className="um-card"><h2>{title}</h2><p className="um-quiet">{body}</p></section>;
}

function SearchBox({ value, onSearch }: { value: string; onSearch: (q: string) => void }) {
  const [draft, setDraft] = useState(value);
  useEffect(() => { setDraft(value); }, [value]);
  return (
    <form className="um-search" role="search" onSubmit={(e) => { e.preventDefault(); onSearch(draft.trim()); }}>
      <input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Resident, building or case reference" aria-label="Find a resident" />
      <button type="submit" className="um-open">Find</button>
    </form>
  );
}

/**
 * Three views (Tide phase 2, 2026-10-04), in the URL:
 *   board      the Monday Finance board as a read model - the default
 *   handoffs   ?view=handoffs: the UMED approvals handed to Finance
 *   search     ?view=search[&q=] or ?resident=: find a resident, open the dossier
 *   atd        ?view=atd: ATD for EVERY current resident, finance account or not (2026-10-13)
 * A link that names a resident (a handoff, a pre-board URL) still lands on that
 * resident. A bare ?q= belongs to the board, whose own search box writes it, so
 * the resident search always names its view; inferring "search" from q sent a
 * board search out of the board.
 */
type View = 'board' | 'handoffs' | 'search' | 'recon' | 'atd' | 'resubmittals' | 'ledger' | 'deaths';
const VIEW_TITLE: Record<View, string> = { board: 'Finance board', handoffs: 'Handed to Finance', search: 'Residents', recon: 'Cutover reconciliation', atd: 'ATD — all residents', resubmittals: 'Reversals Submitted', ledger: 'Payments & invoices', deaths: 'Death reports' };

export default function FinanceShell() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const q = params.get('q') || '';
  const residentId = params.get('resident');
  const asked = params.get('view');
  const view: View = asked === 'handoffs' || asked === 'search' || asked === 'board' || asked === 'recon' || asked === 'atd' || asked === 'resubmittals' || asked === 'ledger' || asked === 'deaths'
    ? asked
    : (residentId ? 'search' : 'board');
  const go = (next: { q?: string; resident?: string | null }) => {
    const p = new URLSearchParams();
    const nq = next.q === undefined ? q : next.q;
    const nr = next.resident === undefined ? residentId : next.resident;
    if (!nr) p.set('view', 'search');
    if (nq) p.set('q', nq);
    if (nr) p.set('resident', nr);
    navigate(`${BASE}?${p}`);
  };
  const viewHref = (v: View) => (v === 'board' ? BASE : `${BASE}?view=${v}`);
  const setView = (v: View) => navigate(viewHref(v));
  /**
   * The board view's layout: absent = the Kanban, the default since Isaiah Cotto's ruling of 2026-10-14; ?layout=table =
   * the table; an old ?layout=kanban link still works (boardLayoutOf). Switching keeps the search and filters and drops
   * what belongs to one layout (the table's tab, page, sort; the open record). From another view, the board starts clean.
   */
  const layout: BoardLayout = useBoardLayout(view === 'board');
  const layoutHref = (l: BoardLayout, tab?: string) => {
    const p = view === 'board' ? new URLSearchParams(params) : new URLSearchParams();
    for (const k of ['view', 'resident', 'layout', 'tab', 'page', 'sort', 'dir', 'open']) p.delete(k);
    if (l === 'table') p.set('layout', 'table');
    if (tab) p.set('tab', tab);
    const qs = p.toString();
    return `${BASE}${qs ? `?${qs}` : ''}`;
  };
  const setLayout = (l: BoardLayout, tab?: string) => navigate(layoutHref(l, tab));

  const [scope, setScope] = useState<FinanceScope | null>(null);
  const [refusal, setRefusal] = useState<{ title: string; body: string } | null>(null);
  const [hits, setHits] = useState<ResidentHit[] | null>(null);
  const [hitsErr, setHitsErr] = useState<string | null>(null);
  const [ctx, setCtx] = useState<ResidentContext | null>(null);
  const [ctxErr, setCtxErr] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // Finance cutover P0: offered only when the server registers the recon routes AND a recon capability is held.
  const reconAvailable = useReconAvailable(Boolean(scope));
  // "ATD — all residents" is RESIDENT_ATD_MARK's (verifier on #399): its doors are drawn only for a caller who holds it.
  // One GET /atd/meta for the shell: the doors to "ATD — all residents", and the Kanban's "Mark ATD…" (2026-10-15).
  const atdCan = useAtdCan(Boolean(scope));
  const atdAll = atdCan.allResidents;
  // Providers' death reports waiting on Finance (ruling 2026-10-22): read only for a caller who may confirm them.
  const deathsOpen = Boolean(scope && scope.can?.report_deceased);
  const [deathCount, setDeathCount] = useDeathRequestCount(deathsOpen);

  useEffect(() => {
    (async () => {
      try {
        const r = await financeApi.scope();
        if (!r.success || !r.data) { setRefusal(r.code === 'DENY_NO_CAPABILITY' ? { title: 'Not a Finance workspace user', body: 'Your role does not include the Finance resident context. Ask your administrator to ratify a Finance role.' } : { title: 'Could not open Finance', body: outcomeOf(r) }); return; }
        setScope(r.data);
      } catch { setRefusal({ title: 'Could not reach the server', body: 'Try again in a moment.' }); }
    })();
  }, []);

  useEffect(() => {
    if (!scope || !q || view !== 'search') { setHits(null); setHitsErr(null); return; }
    let live = true;
    (async () => {
      const r = await financeApi.search(q);
      if (!live) return;
      if (r.success && r.data) { setHits(r.data.items); setHitsErr(null); return; }
      // Never `[]` on a failure: that renders "No resident matches in your
      // scope", which asserts something about the resident when the truth is
      // that the search did not complete.
      setHits(null);
      setHitsErr(outcomeOf(r));
    })();
    return () => { live = false; };
  }, [scope, q, view]);

  const loadContext = useCallback(async () => {
    if (!residentId || view !== 'search') { setCtx(null); return; }
    setCtxErr(null);
    try {
      const r = await financeApi.resident(residentId);
      if (!r.success || !r.data) { setCtx(null); setCtxErr(r.code === 'NOT_FOUND' ? 'No such resident here. They may be in a building outside your scope.' : outcomeOf(r)); return; }
      setCtx(r.data);
    } catch { setCtx(null); setCtxErr('Could not reach the server.'); }
  }, [residentId, view]);
  useEffect(() => { setNotice(null); if (scope) loadContext(); }, [scope, loadContext]);

  const onChanged = async (msg: string) => { setNotice(msg); await loadContext(); };

  if (refusal) return <div className="um"><main className="um-main one"><Refusal title={refusal.title} body={refusal.body} /></main></div>;
  if (!scope) return <div className="um"><main className="um-main one"><section className="um-card"><p className="um-quiet">Opening Finance…</p></section></main></div>;

  // 2026-10-20: the native ledger's page, for a caller who may import, export or record payments.
  const ledgerOpen = Boolean(scope.can?.import_ledger || scope.can?.export_ledger || scope.can?.record_payments);
  const approvalsOpen = scope.counts.approvals_open;
  const sidebarGroups: SidebarGroup[] = [
    {
      key: 'views', label: 'Views',
      items: [
        { key: 'kanban', label: 'Kanban', icon: SquareKanban, href: layoutHref('kanban'), active: view === 'board' && layout === 'kanban',
          title: 'The Finance board as lanes. A drag never records anything.' },
        { key: 'table', label: 'Table', icon: Table2, href: layoutHref('table'), active: view === 'board' && layout === 'table',
          title: 'The Finance board as one table, by tab.' },
      ],
    },
    {
      key: 'work', label: 'Work',
      items: [
        { key: 'handoffs', label: 'Handed to Finance', icon: HandCoins, href: viewHref('handoffs'), active: view === 'handoffs',
          badge: typeof approvalsOpen === 'number' && approvalsOpen > 0 ? { value: approvalsOpen, label: `${approvalsOpen} open` } : null },
        { key: 'resubmittals', label: 'Reversals Submitted', icon: RotateCcw, href: viewHref('resubmittals'), active: view === 'resubmittals' },
        { key: 'search', label: 'Search', icon: Search, href: viewHref('search'), active: view === 'search' },
        ...((atdAll || view === 'atd') ? [{ key: 'atd', label: 'ATD — all residents', icon: PackageCheck, href: viewHref('atd'), active: view === 'atd' }] : []),
        ...((reconAvailable || view === 'recon') ? [{ key: 'recon', label: 'Cutover reconciliation', icon: Scale, href: viewHref('recon'), active: view === 'recon' }] : []),
        ...(deathsOpen ? [{ key: 'deaths', label: 'Death reports', icon: HeartOff, href: viewHref('deaths'), active: view === 'deaths',
          badge: typeof deathCount === 'number' && deathCount > 0 ? { value: deathCount, label: `${deathCount} waiting on Finance`, tone: 'stop' as const } : null }] : []),
        ...((ledgerOpen || view === 'ledger') ? [{ key: 'ledger', label: 'Payments & invoices', icon: ReceiptText, href: viewHref('ledger'), active: view === 'ledger' }] : []),
      ],
    },
  ];
  return (
    <div className="um board-host">
      <WorkspaceMenu current="FINANCE" />
      <WorkspaceLayout workspace="FINANCE" navLabel="Finance views" groups={sidebarGroups}>
      <header className="um-bar">
        <div className="um-bar-inner">
          <div className="um-ident">
            <div className="wsb-title">
            <SidebarTrigger />
            <div className="um-where">
              <span className="um-eyebrow">eMerge · Finance</span>
              <h1>{VIEW_TITLE[view]}</h1>
              <span className="um-sub">{scope.enterprise ? 'Every building' : `${scope.facility_count} ${scope.facility_count === 1 ? 'building' : 'buildings'}`} · {scope.counts.expedited_pending} expedited {scope.counts.expedited_pending === 1 ? 'request' : 'requests'} with Scheduling</span>
            </div>
            <div className="um-aside"><FindResidentEntry /></div>
            </div>
          </div>
        </div>
      </header>
      <main className={`um-main${view === 'search' ? '' : ' one'}${view === 'board' || view === 'atd' || view === 'resubmittals' ? ' board-main' : ''}`}>
        {view === 'board' && layout === 'table' && (
          <FinanceBoard scope={scope} onAtdAll={atdAll ? () => setView('atd') : undefined} />
        )}
        {view === 'board' && layout === 'kanban' && <FinanceKanban scope={scope} canMarkAtd={atdCan.mark} onElsewhere={(tab) => setLayout('table', tab)} />}
        {view === 'resubmittals' && <ResubmittalsKanban />}
        {view === 'atd' && <AtdResidentsBoard idBase="fn-atd-all" from="Finance" />}
        {view === 'recon' && <CutoverReconciliation />}
        {view === 'ledger' && <LedgerImportPage scope={scope} />}
        {view === 'deaths' && (deathsOpen
          ? <DeathReports onCount={setDeathCount} />
          : <Refusal title="Death reports" body="Recording a death is not open to this account here." />)}
        {view === 'handoffs' && <Handoffs onOpen={(id) => go({ resident: id })} canSendBack={Boolean(scope.can?.send_back)} canManage={Boolean(scope.can?.manage_handoff)} />}
        {view === 'search' && (
          <>
            <div className="um-list">
              <SearchBox value={q} onSearch={(next) => go({ q: next, resident: null })} />
              {!q && <p className="um-quiet">Find a resident by name, or a building by name, to open their Finance context.</p>}
              {q && hitsErr && <p className="um-notice stop" role="alert">{hitsErr}</p>}
              {q && !hitsErr && hits === null && <p className="um-quiet">Searching…</p>}
              {q && !hitsErr && hits && hits.length === 0 && <p className="um-quiet">No resident matches in your scope.</p>}
              {hits && hits.length > 0 && (
                <ul className="fn-rows" aria-label="Residents">
                  {hits.map((h) => (
                    <li key={h.id} className={h.id === residentId ? 'selected' : ''}>
                      <button type="button" className="fn-row" onClick={() => go({ resident: h.id })} aria-current={h.id === residentId ? 'true' : undefined}>
                        <span className="who">{h.name}</span>
                        <span className="where"><BuildingOrClinic row={h} fallback="No current building" /></span>
                        <span className="state">{hitStateWord(h)}{h.remaining_amount !== null ? ` · Monday: ${money(h.remaining_amount)} remaining${h.remaining_as_of ? ` (as of ${fmtDate(h.remaining_as_of.as_of_day || h.remaining_as_of.as_of)})` : ' (Monday historical mirror)'}` : ''}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <aside className="um-context">
              {ctxErr && <section className="um-card"><p className="um-notice stop" role="alert">{ctxErr}</p></section>}
              {!ctxErr && !ctx && <section className="um-card"><p className="um-empty">Select a resident to see their Finance context.</p></section>}
              {ctx && <Context ctx={ctx} scope={scope} notice={notice} onChanged={onChanged} onReload={async () => { setNotice(null); await loadContext(); }} />}
            </aside>
          </>
        )}
      </main>
      </WorkspaceLayout>
    </div>
  );
}
